import useApiRequest from "@/lib/hooks/use-request";
import { UserProfileResponseDto } from "@/lib/interfaces/user";
import { Badge } from "@/ui-components/ui/badge";
import { Button } from "@/ui-components/ui/button";
import { CardDescription, CardTitle } from "@/ui-components/ui/card";
import ConfirmationDialog from "@/ui-components/ui/confirmation-dialog";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Separator } from "@/ui-components/ui/separator";
import { Table, TableBody, TableCell, TableRow } from "@/ui-components/ui/table";
import { PageTitle, PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";
import { User } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

const PersonalSettings = () => {
  const [userProfile, setUserProfile] = useState<UserProfileResponseDto>();
  const [isLoading, setIsLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const { apiRequest } = useApiRequest();

  const getUserProfile = useCallback(async () => {
    setIsLoading(true);
    const res = await apiRequest<UserProfileResponseDto>("user-profile", "GET");
    setIsLoading(false);
    if (res.data) {
      setUserProfile(res.data);
    }
  }, [apiRequest]);

  useEffect(() => {
    getUserProfile();
  }, [getUserProfile, reload]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={userProfile}
      renderElement={(item: UserProfileResponseDto) => (
        <PersonalSettingsDetails userProfile={item} setReload={setReload} />
      )}
    />
  );
};

const PersonalSettingsDetails = ({
  userProfile,
  setReload,
}: {
  userProfile: UserProfileResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  // const [isEditing, setIsEditing] = useState(false);
  const { apiRequest } = useApiRequest();

  const resetPassword = async () => {
    await apiRequest("user-profile/reset-password", "POST", {
      toast: {
        toastText: "Passwort zurücksetzen E-Mail wurde erfolgreich gesendet",
      },
    });
  };

  return (
    <div>
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center">
          <PageTitle>Mein Account</PageTitle>
          <div className="flex gap-2">
            <ConfirmationDialog
              title="Passwort zurücksetzen"
              description="Sind Sie sicher, dass Sie Ihr Passwort zurücksetzen möchten? Sie erhalten eine E-Mail mit Anweisungen."
              confirmText="Passwort zurücksetzen"
              onConfirm={resetPassword}
              variant="destructive"
            >
              <Button variant="destructive">Passwort zurücksetzen</Button>
            </ConfirmationDialog>
            {/* <Button onClick={() => setIsEditing(!isEditing)} className="gap-2" variant="secondary">
              <span>{isEditing ? "Abbrechen" : "Bearbeiten"}</span>
              {isEditing ? <X size={16} /> : <Edit2 size={16} />}
            </Button> */}
          </div>
        </div>
      </PageTitleContainer>
      <div>
        {/* {isEditing ? (
          <PersonalSettingsEditor
            item={userProfile}
            onSubmitDone={() => {
              setIsEditing(false);
              setReload((prev) => !prev);
            }}
          />
        ) : ( */}
        <div className="w-1/2 m-auto mt-8">
          <div className="space-y-4">
            <div className="flex justify-between">
              <div className="items-center flex space-x-4">
                <div className="space-y-1">
                  <CardTitle className="text-2xl">
                    {userProfile.firstName} {userProfile.lastName}
                  </CardTitle>
                  <CardDescription>{userProfile.mail}</CardDescription>
                  <Badge variant="secondary" className="mt-1">
                    {userProfile.role}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
          <Separator className="my-6" />
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Vorname</TableCell>
                <TableCell>{userProfile.firstName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Nachname</TableCell>
                <TableCell>{userProfile.lastName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Email</TableCell>
                <TableCell>{userProfile.mail}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Vertriebsrang</TableCell>
                <TableCell>{userProfile.belongsToSalesRankTitle ?? "Kein Vertriebsrang zugewiesen"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Upline Hierarchie</TableCell>
                <TableCell>
                  {userProfile.uplineHierarchy.length > 0 ? (
                    <ul className="space-y-2 list-none p-0">
                      {userProfile.uplineHierarchy.map((user, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <User size={16} />
                          {user.value} - {user.salesRankTitle}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="italic">Keine Vertriebspartner in der Upline</span>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Downline Hierarchie</TableCell>
                <TableCell>
                  {userProfile.downlineHierarchy && userProfile.downlineHierarchy.length > 0 ? (
                    <ul className="space-y-2 list-none p-0">
                      {userProfile.downlineHierarchy.map((user, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <User size={16} />
                          {user.value} - {user.salesRankTitle}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="italic">Keine Vertriebspartner in der Downline</span>
                  )}
                </TableCell>
              </TableRow>
              
            </TableBody>
          </Table>
        </div>
        {/* )} */}
      </div>
    </div>
  );
};

export default PersonalSettings;
